import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { ASSET_HIERARCHY_IMAGE } from '../queries';
import insuranceTypeNames from 'constants/insuranceTypeNames';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { ownInsurance } from 'constants/ownInsurance';
import InsuranceSummary from './InsuranceSummary';
import { useInsuranceTypeConfiguration } from 'utils/hooks/useInsuranceTypeConfiguration';
import { useReactiveVar } from '@apollo/client';
import { commonGeneralDataVar } from '../variables';
import { StyledImage, FigureWrapper } from './InsuranceStyles';

const DocumentDetailSummary = ({ documentDetail }) => {
  const { t } = useTranslation();

  const insuranceTypeConfig = useInsuranceTypeConfiguration();
  const commonGeneralData = useReactiveVar(commonGeneralDataVar);

  const { data } = useQueryWithErrorHandling(ASSET_HIERARCHY_IMAGE, {
    skip: !documentDetail.assetHierarchy?.id,
    variables: { id: documentDetail.assetHierarchy?.id },
    cacheEviction: true
  });

  const insuranceSimulations = documentDetail.insuranceSimulations;
  const insuranceSimulationForHeader = useMemo(() => {
    return insuranceSimulations.some(item => item.insuranceTypeCode === insuranceTypeConfig.casco?.propertyCode)
      ? insuranceSimulations.find(item => item.insuranceTypeCode === insuranceTypeConfig.casco?.propertyCode)
      : insuranceSimulations[0];
  }, [insuranceSimulations, insuranceTypeConfig]);

  const getInsuranceSummary = useCallback(
    (item, idx) => {
      const ownInsuranceValue =
        item.insuranceTypeCode === insuranceTypeConfig.rca?.propertyCode ? commonGeneralData.ownRca : commonGeneralData.ownCasco;
      return (
        <>
          <Grid container key={idx} mt={4}>
            <Grid item pr={2}>
              <Typography fontWeight='bold' color='primary'>
                {item.insuranceTypeName}
              </Typography>
            </Grid>
            {ownInsuranceValue === ownInsurance.NoInsurance ? (
              <Typography>{t('Offer.Insurance.NoInsurance')}</Typography>
            ) : ownInsuranceValue === ownInsurance.FirstYear || ownInsuranceValue === ownInsurance.EntirePeriod ? (
              <Typography>{t('Offer.Insurance.OwnInsuranceSelected')}</Typography>
            ) : !item.selectedQuotation ? (
              <Typography>{t('Offer.Insurance.NoSelectedQuotation')}</Typography>
            ) : (
              <InsuranceSummary
                insurerName={item.selectedQuotation?.insurerName}
                insuranceAmount={
                  item.selectedQuotation?.cascoQuotation
                    ? item.selectedQuotation.cascoQuotation?.insuranceAmount
                    : item.selectedQuotation?.rcaQuotation?.selectedDetail?.insuranceAmount
                }
                currencyCode={item.selectedQuotation.currencyCode}
                periodicity={item.selectedQuotation?.cascoQuotation?.periodicity || item.periodicity}
              />
            )}
          </Grid>
          {item.insuranceTypeCode === insuranceTypeConfig.casco?.propertyCode &&
            item.rtiOption &&
            item.rtiOption.insurerName !== null &&
            item.rtiOption.insuranceAmount !== null && (
              <Grid container mt={4}>
                <Grid item xs={12} sm={2} md={1}>
                  <Typography fontWeight='bold' color='primary'>
                    {insuranceTypeNames.RTI}
                  </Typography>
                </Grid>
                <>
                  {item.hasRtiOption ? (
                    <InsuranceSummary
                      insurerName={item.rtiOption.insurerName}
                      insuranceAmount={item.rtiOption.insuranceAmount}
                      currencyCode={item.selectedQuotation?.currencyCode}
                      periodicity={item.rtiOption.periodicity}
                    />
                  ) : (
                    t('Offer.Insurance.NoSelectedQuotation')
                  )}
                </>
              </Grid>
            )}
        </>
      );
    },
    [commonGeneralData, insuranceTypeConfig, t]
  );

  return (
    <Grid container mt={3} spacing={2}>
      <Typography fontWeight='bold' color='primary'>
        <span>
          {documentDetail.item.typeName} {'|'} {`${documentDetail.item.name} x ${documentDetail.quantity}` || ''} {'|'}{' '}
          {(insuranceSimulationForHeader &&
            `${t('DECIMAL', { number: insuranceSimulationForHeader?.insuredAmount })} 
            ${insuranceSimulationForHeader?.currencyCode}`) ||
            ''}
        </span>
      </Typography>
      <Grid item xs={12} md={8} lg={10}>
        {insuranceSimulations.length > 0 &&
          [...insuranceSimulations].reverse().map((item, idx) => <div key={item.id}>{getInsuranceSummary(item, idx)}</div>)}
      </Grid>
      <Grid item container xs={12} md={4} lg={2} justifyContent='center' alignItems='center'>
        <FigureWrapper>
          {data?.assetHierarchy?.imageUrl && <StyledImage src={data?.assetHierarchy?.imageUrl} alt='...' />}
          {documentDetail.chassisNumber && (
            <figcaption style={{ textAlign: 'center', fontWeight: 'bold' }}>{documentDetail.chassisNumber}</figcaption>
          )}
        </FigureWrapper>
      </Grid>
    </Grid>
  );
};

DocumentDetailSummary.propTypes = {
  documentDetail: PropTypes.object.isRequired
};

export default DocumentDetailSummary;
